import { createReducer, on } from '@ngrx/store';
import {
    isIdemSale,
    updateChild,
} from 'shared/ui/tables/table-grouped-by-category/entityWithChildrenUtils';
import * as TotalGrossActions from '../total-gross/total-gross.actions';
import * as OtherVendorsSalesActions from './other-vendors-sales.actions';
import { OtherVendorsSale } from './other-vendors-sales.models';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface OtherVendorsSalesState {
    otherVendorsSales: OtherVendorsSale[];
    otherVendorsSale: OtherVendorsSale | null;
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialOtherVendorsSalesState: OtherVendorsSalesState = {
    otherVendorsSales: [],
    otherVendorsSale: null,
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

// All OtherVendorsSales Reducer
export const otherVendorsSalesReducer = createReducer(
    initialOtherVendorsSalesState,

    // On Load OtherVendorsSales
    on(OtherVendorsSalesActions.loadOtherVendorsSales, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        OtherVendorsSalesActions.loadOtherVendorsSalesSuccess,
        (state, { otherVendorsSales }) => ({
            ...state,
            isLoading: false,
            otherVendorsSales: otherVendorsSales,
        })
    ),
    on(
        OtherVendorsSalesActions.loadOtherVendorsSalesFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On Clear
    on(OtherVendorsSalesActions.clearOtherVendorsSales, (state) => ({
        ...state,
        otherVendorsSales: [],
        otherVendorsSale: null,
        isLoading: false,
        error: null,
    })),

    // On Load Single OtherVendorsSale
    on(OtherVendorsSalesActions.loadOtherVendorsSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        OtherVendorsSalesActions.loadOtherVendorsSaleSuccess,
        (state, { otherVendorsSale }) => ({
            ...state,
            isLoading: false,
            otherVendorsSale: otherVendorsSale,
        })
    ),
    on(
        OtherVendorsSalesActions.loadOtherVendorsSaleFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On Create
    on(OtherVendorsSalesActions.createOtherVendorsSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        OtherVendorsSalesActions.createOtherVendorsSaleSuccess,
        (state, { otherVendorsSale }) => ({
            ...state,
            isLoading: false,
            otherVendorsSales: ((): OtherVendorsSale[] => {
                const sales = state.otherVendorsSales || [];
                let updateSales = [];
                sales.some(
                    (sale) => sale.location.id === otherVendorsSale.location.id
                )
                    ? (updateSales = sales.map((sale: OtherVendorsSale) =>
                          sale.location.id === otherVendorsSale.location.id
                              ? {
                                    // Spread the old sale to keep old cash
                                    // and credit card
                                    ...sale,
                                    children: [
                                        ...sale.children,
                                        ...otherVendorsSale.children,
                                    ],
                                }
                              : sale
                      ))
                    : (updateSales = [...sales, otherVendorsSale]);

                return updateSales;
            })(),
        })
    ),
    on(
        OtherVendorsSalesActions.createOtherVendorsSaleFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On Update
    on(OtherVendorsSalesActions.updateOtherVendorsSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        OtherVendorsSalesActions.updateOtherVendorsSaleSuccess,
        (state, { otherVendorsSale }) => ({
            ...state,
            isLoading: false,
            otherVendorsSales: state.otherVendorsSales.map(
                (sale: OtherVendorsSale) =>
                    isIdemSale(sale, otherVendorsSale)
                        ? {
                              ...otherVendorsSale,
                              children: updateChild(sale, otherVendorsSale),
                          }
                        : sale
            ),
        })
    ),
    on(
        OtherVendorsSalesActions.updateOtherVendorsSaleFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On Delete
    on(OtherVendorsSalesActions.deleteOtherVendorsSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        OtherVendorsSalesActions.deleteOtherVendorsSaleSuccess,
        (state, { deletedSales }) => ({
            ...state,
            isLoading: false,
            otherVendorsSales: state.otherVendorsSales.filter(
                (sales: OtherVendorsSale) =>
                    sales.children.every(
                        (child) => child.saleId !== deletedSales[0]
                    )
            ),
        })
    ),
    on(
        OtherVendorsSalesActions.deleteOtherVendorsSaleFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On Delete Item
    on(OtherVendorsSalesActions.deleteOtherVendorsSaleItem, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        OtherVendorsSalesActions.deleteOtherVendorsSaleItemSuccess,
        (state, { deletedSaleItem }) => ({
            ...state,
            isLoading: false,
            otherVendorsSales: state.otherVendorsSales.map(
                (sales: OtherVendorsSale) => {
                    if (
                        sales.children.some(
                            (child) => child.saleId === deletedSaleItem
                        )
                    ) {
                        return {
                            ...sales,
                            children: sales.children.filter(
                                (child) => child.saleId !== deletedSaleItem
                            ),
                        };
                    } else {
                        return sales;
                    }
                }
            ),
        })
    ),
    on(
        OtherVendorsSalesActions.deleteOtherVendorsSaleItemFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On update total gross
    on(
        TotalGrossActions.updateTotalGrossByLocationSuccess,
        (state, { totalGross }) => ({
            ...state,
            isLoading: false,
            otherVendorsSales: state.otherVendorsSales.map(
                (sales: OtherVendorsSale) =>
                    sales.location.id === totalGross.location_id
                        ? {
                              ...sales,
                              cash: totalGross.cash,
                              credit_card: totalGross.credit_card,
                              totals: {
                                  commission_rate: totalGross.commission_rate,
                                  commission_amount:
                                      totalGross.commission_amount,
                              },
                          }
                        : sales
            ),
        })
    )
);
